import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useDispatch, useSelector } from 'react-redux';
import NotificationAlertHandler from '../NotificationAlertHandler/NotificationAlertHandler';
import { ScheduleIndexResponse } from '@/dashboard/services/dashboardApi';
import DashboardGraphLoader from '@/dashboard/components/DashboardGraph/DashboardGraphLoader';
import DashboardErrorBoundary from '@/dashboard/components/DashboardErrorBoundary';
import SchoolYearSelector from '@/components/SchoolYearSelector/SchoolYearSelector';
import MainWrapper from '@/components/Layout/MainWrapper';
import { useTablet } from '@/hooks/responsiveHooks';
import tokens from '@/dashboard/tokens.json';
import { usePageTitle } from '@/hooks/usePageTitle';
import { PAGE_TITLES } from '@/core/utils/app.constants';
import Header from '@/components/Header/Header';
import { dashboardApi, isValidSchedulesIndexResponse } from '@/dashboard/services/dashboardApi';
import { DashboardContextProvider } from '@/dashboard/provider/DashboardContextProvider';
import { selectSchoolYearFromStore, setSeletedYear } from '@/dashboard/redux/dashboardReducer';
import { reviewPayrollApi } from '@/review-payroll/services/reviewPayrollApi';
import { API_CACHE_KEYS } from '@/core/network/baseAppApi';
import { getBoardId, getUserPortalRole } from '@/core/web-storage/webStorage.utils';
import { PORTAL_ROLES } from '@/authorization/authorization.constants';
import AppErrorBoundary from '@/components/Error/AppErrorBoundary';

function DashboardContent({
  data,
  schoolYear,
  onSchoolYearChange,
  isDiscardingUpload,
  schedulesError,
}: {
  data: ScheduleIndexResponse | undefined;
  schoolYear: number | null;
  onSchoolYearChange: (year: number) => void;
  isDiscardingUpload: boolean;
  schedulesError: string | undefined;
}) {
  return (
    <Box display="flex" flexDirection="column" gap={4} height="100%" pb={2.5}>
      <NotificationAlertHandler />

      <AppErrorBoundary FallbackComponent={DashboardErrorBoundary}>
        <Box
          display="flex"
          flexDirection="column"
          gap={4}
          height="100%"
          sx={{ overflowY: 'visible' }}
        >
          {schedulesError ? (
            <Typography>{schedulesError}</Typography>
          ) : (
            <>
              <Box
                className="button-wrapper"
                display="flex"
                gap={6}
                mb={4}
                alignItems="center"
                flexWrap="wrap"
              >
                {data && schoolYear && isValidSchedulesIndexResponse(data) ? (
                  <SchoolYearSelector
                    schedules={data}
                    schoolYear={schoolYear}
                    onChange={onSchoolYearChange}
                    allYearRequired={false}
                  />
                ) : null}
              </Box>
              <DashboardGraphLoader selectedYear={isDiscardingUpload ? null : schoolYear} />
            </>
          )}
        </Box>
      </AppErrorBoundary>
    </Box>
  );
}
function Dashboard() {
  usePageTitle(PAGE_TITLES.DASHBOARD);
  const isTablet = useTablet();
  const dispatch = useDispatch();

  const { isSuccess, data, error, isError } = dashboardApi.useGetSchedulesIndexQuery();
  const [schoolYear, setSchoolYear] = useState<number | null>(null);
  const [schedulesError, setSchedulesError] = useState<string | undefined>(undefined);
  const yearFromStore = useSelector(selectSchoolYearFromStore);
  const [, { isLoading: isDiscardingUpload }] = reviewPayrollApi.useDiscardUploadMutation({
    fixedCacheKey: API_CACHE_KEYS.DISCARD_UPLOAD,
  });
  function onSchoolYearChange(year: number) {
    setSchoolYear(year);
  }

  useEffect(() => {
    if (isError) {
      const userPortalRole = getUserPortalRole();
      const userBoardId = getBoardId();
      if (!userBoardId && userPortalRole == PORTAL_ROLES.ATRF) {
        setSchedulesError('');
      } else {
        setSchedulesError(error as string);
      }
    } else if (isSuccess) {
      if (data === undefined || data === null || (Array.isArray(data) && data.length === 0)) {
        setSchedulesError(tokens['NO_SCHEDULES']);
      } else {
        const isValid = isValidSchedulesIndexResponse(data);
        if (isValid) {
          const isDataContainAnyScheduleofYearFromStore = data.some(
            (schedule) => schedule.startYear === yearFromStore,
          );
          if (yearFromStore && isDataContainAnyScheduleofYearFromStore) {
            setSchoolYear(yearFromStore);
          } else {
            setSchoolYear(data[0].startYear);
          }
        } else {
          setSchedulesError(tokens['GENERAL_ERROR']);
        }
      }
    }
  }, [isError, isSuccess, data]);

  useEffect(() => {
    return () => {
      if (schoolYear) {
        dispatch(setSeletedYear(schoolYear));
      }
    };
  }, [schoolYear]);

  return (
    <MainWrapper
      onlyMain={false}
      mainProps={{
        minHeight: '18.75rem',
      }}
      header={isTablet ? null : <Header isFullPage={false} />}
    >
      <DashboardContextProvider schedules={data}>
        <DashboardContent
          data={data}
          schoolYear={schoolYear}
          onSchoolYearChange={onSchoolYearChange}
          isDiscardingUpload={isDiscardingUpload}
          schedulesError={schedulesError}
        />
      </DashboardContextProvider>
    </MainWrapper>
  );
}

export default Dashboard;
